import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@vd/ui';
import { api } from '../../lib/api';

interface InlineProjectNameProps {
  projectId: string;
  /** Used to invalidate the company-scoped project list cache after rename. */
  companyId?: string;
  name: string;
}

/**
 * Click-to-edit project name. Inherits the parent's typography so the
 * static span and the editor look identical to the surrounding text —
 * drop it inside an h1 (board header) or styled wrapper (settings page)
 * and it adopts that font size / weight / color.
 */
export function InlineProjectName({ projectId, companyId, name }: InlineProjectNameProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(name);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // Tracks whether Escape just initiated a cancel — the Escape key
  // path calls setEditing(false) which unmounts the input, but the
  // unmount also fires onBlur, which would re-enter `commit()` with
  // the stale draft. The ref tells `commit` to bail.
  const cancellingRef = useRef(false);

  // Keep the draft in sync with the upstream name when not editing — covers
  // the case where the name changes via another surface (e.g. the General
  // tab) while this component is mounted but idle.
  useEffect(() => { if (!editing) setDraft(name); }, [name, editing]);

  const mutation = useMutation({
    mutationFn: (next: string) => api.updateProject(projectId, { name: next }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', projectId] });
      if (companyId) qc.invalidateQueries({ queryKey: ['projects', companyId] });
    },
  });

  const start = () => {
    setDraft(name);
    setEditing(true);
    // Focus + select on the next tick so the input is mounted.
    setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.select();
    }, 0);
  };

  const commit = async () => {
    // Escape just fired — the blur that follows the unmount should
    // be a no-op, not a save with the stale draft.
    if (cancellingRef.current) {
      cancellingRef.current = false;
      return;
    }
    const next = draft.trim();
    if (!next || next === name) { setEditing(false); return; }
    setSaving(true);
    try {
      await mutation.mutateAsync(next);
      toast.push({ variant: 'success', title: 'Project renamed' });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Rename failed', description: (e as Error).message });
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  const cancel = () => {
    cancellingRef.current = true;
    setDraft(name);
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        disabled={saving}
        size={Math.max(8, draft.length + 1)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); (e.currentTarget as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { e.preventDefault(); cancel(); }
        }}
        aria-label="Project name"
        style={{
          font: 'inherit',
          color: 'inherit',
          letterSpacing: 'inherit',
          background: 'transparent',
          border: 'none',
          borderBottom: '1px dashed var(--text-tertiary)',
          padding: '0 2px',
          margin: '0 -2px',
          outline: 'none',
        }}
      />
    );
  }

  return (
    <span
      onClick={start}
      title="Click to rename"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); start(); } }}
      style={{
        cursor: 'text',
        borderRadius: 4,
        padding: '0 2px',
        margin: '0 -2px',
        transition: 'background 100ms',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {name}
    </span>
  );
}
